// src/components/layout/header.tsx
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Settings, Headphones, LogOut, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useSupport } from "../../context/SupportContext";
import { useUserDisplayName } from "../../hooks/useUserDisplayName";
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import logonegro from '../../assets/images/logonegro.svg';
import { toast } from 'react-toastify';
import { useWindow } from "../../context/WindowContext";
import { profileService } from "../../services/profile.service";
import { ServerSwitch } from "../ui/ServerSwitch";
interface HeaderProps {
  updateUserPhoto?: (photoURL: string) => Promise<string | void>;
  toggleSidebar;
}

export default function Header({ updateUserPhoto, toggleSidebar }: HeaderProps) {
  const windows= useWindow();
  const navigate = useNavigate();
  const { user, logout, updateUserPhoto: contextUpdateUserPhoto, loadProfileImage } = useAuth();
  const { unreadCount } = useSupport();
  const { displayName } = useUserDisplayName();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);

  // Cargar la imagen de perfil cuando el usuario cambia
  useEffect(() => {
    const loadImage = async () => {
      setIsLoadingImage(true);
      try {
        // Primero intentar cargar desde el servicio de perfil
        const photoUrl = await profileService.getProfilePhotoUrl();
        
        if (photoUrl) {
          console.log('Imagen de perfil cargada desde el backend:', photoUrl);
          setProfileImageUrl(photoUrl);
          
          // Precargar la imagen para verificar que funciona
          const img = new Image();
          img.onerror = async () => {
            console.log('Error cargando imagen, intentando obtener nueva URL firmada...');
            // Si la imagen no carga (posible URL expirada), intentar obtener una nueva
            try {
              const freshUrl = await profileService.getProfilePhotoUrl();
              if (freshUrl && freshUrl !== photoUrl) {
                setProfileImageUrl(freshUrl);
              }
            } catch (error) {
              console.error('Error obteniendo nueva URL firmada:', error);
            }
          };
          img.src = photoUrl;
        } else if (user?.profileImage) {
          // Si no hay foto en el backend, intentar con el método del contexto
          console.log('Intentando cargar imagen de perfil desde contexto:', user.profileImage);
          const imageUrl = await loadProfileImage(user.profileImage);
          
          if (imageUrl) {
            console.log('Imagen de perfil cargada exitosamente:', imageUrl);
            setProfileImageUrl(imageUrl);
          } else {
            setProfileImageUrl(null);
          }
        } else {
          console.log('Usuario no tiene imagen de perfil');
          setProfileImageUrl(null);
        }
      } catch (error) {
        console.error("Error loading profile image:", error);
        setProfileImageUrl(null);
      } finally {
        setIsLoadingImage(false);
      }
    };

    if (user) {
      loadImage();
    }
  }, [user, user?.profileImage, loadProfileImage]);

  // El contador de mensajes no leídos ahora viene del contexto SupportContext
  // que ya maneja la actualización automática cada 30 segundos

  // Obtiene las iniciales del usuario para mostrar cuando no hay foto
  const getUserInitials = () => {
    // Si el displayName contiene espacios (firstName middleName), tomar las iniciales
    const nameParts = displayName.trim().split(' ');
    
    if (nameParts.length >= 2) {
      // Tomar la primera letra del primer nombre y la primera del segundo
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
    } else if (displayName.length >= 2) {
      // Si es un solo nombre, tomar los primeros 2 caracteres
      return displayName.slice(0, 2).toUpperCase();
    } else if (displayName.length === 1) {
      // Si solo tiene 1 carácter, lo duplicamos
      return displayName.toUpperCase() + displayName.toUpperCase();
    }
    // Fallback por si acaso
    return "US";
  };

  // Mantener las configuraciones existentes
  const headerConfig = {
    height: 100,
    borderWidth: 1,
    borderColor: 'transparent',
    paddingX: 24,
  };

  const logoConfig = {
    height: 75,
    marginLeft: 0,
    marginTop: 0,
  };

  const iconsConfig = {
    size: 25,
    spacing: 10,
    padding: 8,
    marginRight: 0,
  };

  const profileConfig = {
    nameSize: 14,
    myProfileSize: 12,
    avatarSize: 64,
    plusButtonSize: 16,
    spacing: 30,
  };

  // Genera un color de fondo basado en el nombre del usuario (consistente)
  const getAvatarBackgroundColor = () => {
    let hash = 0;
    for (let i = 0; i < displayName.length; i++) {
      hash = displayName.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generamos un color en el rango de azules/grises claros para que las iniciales sean legibles
    const h = Math.abs(hash) % 360;
    const s = 30 + (Math.abs(hash) % 30); // Saturación entre 30% y 60%
    const l = 65 + (Math.abs(hash) % 15); // Luminosidad entre 65% y 80%
    
    return `hsl(${h}, ${s}%, ${l}%)`;
  };

  const iconLinkStyle = {
    padding: `${iconsConfig.padding}px`,
    borderRadius: '9999px',
    transition: 'color 0.2s',
    marginRight: `${iconsConfig.spacing}px`,
    background: 'transparent',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent !important',
      outline: 'none',
    },
    '&:focus': {
      outline: 'none',
      backgroundColor: 'transparent !important',
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Resetear estado
    setErrorMessage(null);
    
    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage("Image size exceeds 5MB limit. Please choose a smaller image.");
      return;
    }
    
    // Validar tipo (solo imágenes)
    if (!file.type.startsWith('image/')) {
      setErrorMessage("Please select a valid image file (JPEG, PNG, GIF).");
      return;
    }
    
    // Guardar el archivo para subir luego
    setSelectedFile(file);
    
    // Mostrar vista previa
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target?.result as string);
      setIsModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveImage = async () => {
    if (!selectedFile || !selectedImage) return;
    
    try {
      setIsSaving(true);
      setUploadProgress(0);
      setErrorMessage(null);
      
      // Simular progreso de carga
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);
      
      // Subir la foto al backend
      const response = await profileService.uploadProfilePhoto(selectedFile);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (response.success && response.data) {
        // Actualizar la URL de la foto - usar photoProfileUrl preferentemente
        const responseData = response.data as any;
        const newPhotoUrl = responseData.photoProfileUrl || responseData.photoUrl;
        
        if (newPhotoUrl) {
          console.log('Nueva foto de perfil subida:', newPhotoUrl);
          setProfileImageUrl(newPhotoUrl);
          
          // También actualizar en el contexto de autenticación si es necesario
          if (contextUpdateUserPhoto) {
            await contextUpdateUserPhoto(newPhotoUrl);
          }
          
          // Precargar la nueva imagen para asegurar que funciona
          const img = new Image();
          img.onload = () => {
            console.log('Nueva foto de perfil cargada correctamente');
          };
          img.onerror = () => {
            console.error('Error cargando la nueva foto de perfil');
          };
          img.src = newPhotoUrl;
        }
        
        // Limpiar y cerrar modal
        setTimeout(() => {
          setIsModalOpen(false);
          setSelectedImage(null);
          setSelectedFile(null);
          setUploadProgress(0);
          toast.success("Profile photo updated successfully!");
        }, 500);
      } else {
        throw new Error("Failed to upload photo");
      }
    } catch (error: any) {
      console.error('Error saving profile photo:', error);
      const errorMsg = error.response?.data?.error?.message || error.message || "Failed to update profile photo. Please try again.";
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
      setUploadProgress(0);
    } finally {
      setIsSaving(false);
    }
  };

  // Verificar si el usuario tiene una imagen de perfil válida
  const hasValidProfileImage = () => {
    return !!profileImageUrl;
  };

  return (
    <>
      <header style={{
        height: `${headerConfig.height}px`,
        borderBottom: `${headerConfig.borderWidth}px solid ${headerConfig.borderColor}`,
        paddingRight: `${headerConfig.paddingX}px`,
        paddingLeft: `0px`,
      }}
      className="sm:pt-0 sm:pb-0 pt-6 pb-6 flex items-center justify-between bg-transparent">
        <div className="hidden sm:flex sm:w-1/5 min-w-[275px] sm:items-center sm:justify-center">
          <Link to="/dashboard" className="w-full h-full flex items-center justify-center px-4 py-3">
          <img
            src={logonegro}
            alt="KANZ BANK"
            className="w-auto h-auto max-w-full max-h-full object-contain"
            style={{
              maxHeight: `${logoConfig.height}px`,
              transform: 'scale(1.1)' // 10% más grande en ambas direcciones
            }}
          />
          </Link>
        </div>

        {/* Logo para móvil */}
        <div className="sm:hidden ml-0">
          <Link to="/dashboard">
          <img
            src={logonegro}
            alt="KANZ BANK"
            className="h-11"
          />
          </Link>
        </div>

        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          marginRight: `${iconsConfig.marginRight}px`
        }}>
          <div className="hidden sm:flex sm:items-center sm mr-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to="/dashboard/settings" style={iconLinkStyle}>
                    <Settings style={{ 
                      width: `${iconsConfig.size}px`, 
                      height: `${iconsConfig.size}px`,
                      color: '#666666' 
                    }} />
                  </Link>
                </TooltipTrigger>
                <TooltipContent className="bg-transparent border-0 shadow-none">
                  <p className="text-black text-lg">Settings</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to="/dashboard/support" style={{ ...iconLinkStyle, position: 'relative' }}>
                    <Headphones style={{ 
                      width: `${iconsConfig.size}px`, 
                      height: `${iconsConfig.size}px`,
                      color: '#666666' 
                    }} />
                    {unreadCount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        backgroundColor: '#EF4444',
                        color: 'white',
                        borderRadius: '9999px',
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        border: '2px solid white',
                      }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                </TooltipTrigger>
                <TooltipContent className="bg-transparent border-0 shadow-none">
                  <p style={{ fontSize: `${profileConfig.nameSize}px` }} className="text-black">
                    {t('navigation.support')} {unreadCount > 0 && `(${unreadCount} ${t('support.unread')})`}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={handleLogout} style={iconLinkStyle}>
                    <LogOut style={{ 
                      width: `${iconsConfig.size}px`, 
                      height: `${iconsConfig.size}px`,
                      color: '#666666' 
                    }} />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="bg-transparent border-0 shadow-none">
                  <p style={{ fontSize: `${profileConfig.nameSize}px` }} className="text-black">{t('common.logout')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="hidden sm:flex sm:items-center">
            <div style={{ 
              textAlign: 'right', 
              marginRight: `${profileConfig.spacing}px` 
            }}>
              <div style={{ 
                fontSize: `${profileConfig.nameSize}px`,
                fontWeight: 500,
              }}>
                {displayName}
              </div>
              <Link 
                to="/dashboard/myprofile" 
                style={{
                  fontSize: `${profileConfig.myProfileSize}px`,
                  color: '#6b7280',
                  textDecoration: 'none',
                  background: 'transparent',
                }}
              >
                {t('common.profile')}
              </Link>
            </div>
            <div style={{ position: 'relative' }}>
              <div style={{
                width: `${profileConfig.avatarSize}px`,
                height: `${profileConfig.avatarSize}px`,
                borderRadius: '9999px',
                backgroundColor: hasValidProfileImage() ? '#f3f4f6' : getAvatarBackgroundColor(),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
              }}>
                {isLoadingImage ? (
                  // Mostrar un indicador de carga mientras se carga la imagen
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', borderTop: '2px solid #666', borderRight: '2px solid transparent', animation: 'spin 1s linear infinite' }}></div>
                ) : hasValidProfileImage() ? (
                  <img 
                    src={profileImageUrl || ''}
                    alt={displayName}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: 'rotate(0deg)',
                      imageOrientation: 'from-image'
                    }}
                  />
                ) : (
                  <span style={{ 
                    color: 'white',
                    fontSize: `${profileConfig.nameSize + 4}px`, // Ligeramente más grande
                    fontWeight: 600,
                  }}>
                    {getUserInitials()}
                  </span>
                )}
              </div>
              
              {/* Botón "+" siempre visible para cambiar foto */}
              <div style={{ 
                position: 'absolute',
                bottom: `-${profileConfig.plusButtonSize / 6}px`,
                right: `-${profileConfig.plusButtonSize / 6}px`,
              }}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <label
                        htmlFor="profile-image-upload"
                        style={{
                          width: `${profileConfig.plusButtonSize}px`,
                          height: `${profileConfig.plusButtonSize}px`,
                          backgroundColor: 'white',
                          borderRadius: '9999px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        <span style={{
                          fontSize: `${profileConfig.plusButtonSize / 2}px`,
                          fontWeight: 'bold',
                          color: '#666666',
                        }}>+</span>
                      </label>
                    </TooltipTrigger>
                    <TooltipContent className="bg-transparent border-0 shadow-none">
                      <p style={{ fontSize: `${profileConfig.myProfileSize}px` }} className="text-black">
                        {hasValidProfileImage() ? "Change Photo" : "Add Photo"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <input
                  type="file"
                  id="profile-image-upload"
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleImageSelect}
                />
              </div>
            </div>
          </div>
          <div className=" sm:hidden">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleSidebar}
                  style={{
                    ...iconLinkStyle,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                  aria-label="Toggle Menu"
                >
                  <span style={{
                    width: `${iconsConfig.size}px`,
                    height: `${iconsConfig.size}px`,
                    fontSize: `${iconsConfig.size}px`,
                    color: '#666666',
                    display: 'inline-block',
                    textAlign: 'center',
                    lineHeight: `${iconsConfig.size}px`,
                  }}>
                    &#9776;
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="bg-transparent border-0 shadow-none">
                <p className="text-black text-lg">Menu</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          </div>

        </div>
      </header>
      
      {/* Modal para edición de foto */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}>
              <h3 style={{ margin: 0 }}>Profile Photo</h3>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedImage(null);
                  setSelectedFile(null);
                  setUploadProgress(0);
                  setErrorMessage(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {selectedImage && (
              <div style={{
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                overflow: 'hidden',
                margin: '0 auto 20px',
                border: '1px solid #e5e7eb',
              }}>
                <img
                  src={selectedImage}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            )}

            {/* Mensaje de error */}
            {errorMessage && (
              <div style={{
                backgroundColor: '#fee2e2',
                color: '#b91c1c',
                padding: '8px 12px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px',
              }}>
                {errorMessage}
              </div>
            )}

            {/* Barra de progreso */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: '#e5e7eb',
                borderRadius: '3px',
                marginBottom: '20px',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${uploadProgress}%`,
                  height: '100%',
                  backgroundColor: '#2563eb',
                  transition: 'width 0.3s ease',
                }} />
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: hasValidProfileImage() && !selectedImage ? 'space-between' : 'flex-end',
              gap: '12px',
            }}>
              {/* Delete button - only show if user has a photo and not selecting a new one */}
              {hasValidProfileImage() && !selectedImage && (
                <button
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to remove your profile photo?')) {
                      try {
                        setIsDeleting(true);
                        setErrorMessage(null);
                        await profileService.deleteProfilePhoto();
                        setProfileImageUrl(null);
                        setIsModalOpen(false);
                        toast.success('Profile photo removed successfully!');
                      } catch (error) {
                        console.error('Error deleting photo:', error);
                        setErrorMessage('Failed to remove photo. Please try again.');
                      } finally {
                        setIsDeleting(false);
                      }
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '1px solid #fee2e2',
                    background: '#fef2f2',
                    color: '#b91c1c',
                    cursor: 'pointer',
                    opacity: isDeleting ? 0.7 : 1,
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Removing...' : 'Remove Photo'}
                </button>
              )}
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedImage(null);
                    setSelectedFile(null);
                    setUploadProgress(0);
                    setErrorMessage(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                    background: '#f9fafb',
                    cursor: 'pointer',
                  }}
                  disabled={isSaving || isDeleting}
                >
                  Cancel
                </button>
                {selectedImage && (
                  <button
                    onClick={handleSaveImage}
                    disabled={isSaving}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: '#2563eb',
                      color: 'white',
                      cursor: 'pointer',
                      opacity: isSaving ? 0.7 : 1,
                    }}
                  >
                    {isSaving ? (uploadProgress > 0 ? `Uploading: ${uploadProgress}%` : 'Processing...') : 'Save Photo'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}